import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, CircularProgress,
  Stack, Button, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Card, CardContent, Grid, Divider, TextField, Tooltip, Snackbar, Alert
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExploreIcon from '@mui/icons-material/Explore';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import { getEasoGuests, updateGuest, syncConfirmedGuests } from '../../api/guestAPI';
import { getOperationsByGuest } from '../../api/operationAPI';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setSelectedquerie } from '../../reduxcomponents/slices/queriesSlice';

const CustomToolbar = () => {
  return (
    <GridToolbarContainer sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <GridToolbarFilterButton sx={{ color: '#1e40af', fontWeight: 600 }} />
        <GridToolbarExport sx={{ color: '#1e40af', fontWeight: 600 }} />
      </Box>
      <GridToolbarQuickFilter 
        sx={{ 
          pb: 0,
          '& .MuiInputBase-root': {
            bgcolor: '#f8fafc',
            borderRadius: '8px',
            px: 2,
            py: 0.5,
            border: '1px solid #e2e8f0',
            '&::before': { display: 'none' },
            '&::after': { display: 'none' },
          }
        }} 
      />
    </GridToolbarContainer>
  );
};

const EasoGuestsPage = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Trips Modal State
  const [tripsModalOpen, setTripsModalOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestTrips, setGuestTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);

  // Edit Guest State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '', address: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const handleOpenEdit = (guest) => {
    setEditingGuest(guest);
    setEditForm({
      name: guest.name || '',
      phone: guest.phone || '',
      email: guest.email || '',
      address: guest.address || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingGuest) return;
    setSavingEdit(true);
    try {
      await updateGuest(editingGuest.id || editingGuest._id, editForm);
      setEditDialogOpen(false);
      fetchEasoGuests();
    } catch (err) {
      console.error('Failed to update guest:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSyncConfirmed = async () => {
    setSyncing(true);
    try {
      const res = await syncConfirmedGuests();
      setSnack({
        open: true,
        msg: res.message || 'All confirmed leads synced with Easo Guest list!',
        severity: 'success'
      });
      await fetchEasoGuests();
    } catch (err) {
      console.error('Failed to sync confirmed leads:', err);
      setSnack({
        open: true,
        msg: err.message || 'Failed to sync confirmed leads',
        severity: 'error'
      });
    } finally {
      setSyncing(false);
    }
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchEasoGuests = async () => {
    setLoading(true);
    try {
      const response = await getEasoGuests();
      if (response.success) {
        setGuests(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch Easo Guests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEasoGuests();
  }, []);

  const handleOpenTrips = async (guest) => {
    setSelectedGuest(guest);
    setTripsModalOpen(true);
    setLoadingTrips(true);
    try {
      const trips = await getOperationsByGuest(guest.id);
      setGuestTrips(trips);
    } catch (error) {
      console.error("Failed to fetch guest trips", error);
    } finally {
      setLoadingTrips(false);
    }
  };

  const handleViewSingleTrip = (tripId) => {
    dispatch(setSelectedquerie({ _id: tripId }));
    navigate(`/query/view/${tripId}`);
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Guest', 
      flex: 1.2, 
      minWidth: 220,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ height: '100%' }}>
          <Avatar sx={{ 
            color: '#1e40af', 
            bgcolor: '#eff6ff',
            width: 40, 
            height: 40, 
            fontSize: '1.1rem', 
            fontWeight: 800,
            boxShadow: '0 2px 8px rgba(59,130,246,0.15)'
          }}>
            {params.value?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={700} color="#1e293b" noWrap sx={{ textOverflow: 'ellipsis' }}>
              {params.value}
            </Typography>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b' }}>
              ID: <span style={{ fontWeight: 600, color: '#475569' }}>{params.row.guest_id || 'N/A'}</span>
            </Typography>
          </Box>
        </Stack>
      )
    },
    { 
      field: 'contact', 
      headerName: 'Contact Details', 
      flex: 1.5, 
      minWidth: 250,
      renderCell: (params) => (
        <Stack justifyContent="center" sx={{ height: '100%', py: 1 }} spacing={0.8}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#334155', fontWeight: 500 }}>
            <MailOutlineIcon sx={{ fontSize: 16, color: '#3b82f6' }} /> {params.row.email || 'No email provided'}
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#334155', fontWeight: 500 }}>
            <PhoneIcon sx={{ fontSize: 16, color: '#10b981' }} /> {params.row.country_code || ''} {params.row.phone || 'N/A'}
          </Typography>
        </Stack>
      )
    },
    { 
      field: 'trip_count', 
      headerName: 'Total Trips', 
      flex: 0.8, 
      minWidth: 140,
      renderCell: (params) => {
        const count = params.row.all_trips ? params.row.all_trips.length : 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip 
              icon={<FlightTakeoffIcon sx={{ fontSize: '16px !important' }} />} 
              label={`${count} Trip${count !== 1 ? 's' : ''}`}
              size="small"
              onClick={() => handleOpenTrips(params.row)}
              sx={{ 
                bgcolor: count > 0 ? '#ecfdf5' : '#f8fafc',
                color: count > 0 ? '#059669' : '#64748b',
                fontWeight: 700,
                borderRadius: '8px',
                px: 1,
                border: `1px solid ${count > 0 ? '#a7f3d0' : '#e2e8f0'}`,
                '& .MuiChip-icon': { color: 'inherit' },
                boxShadow: count > 0 ? '0 2px 6px rgba(16,185,129,0.1)' : 'none',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: count > 0 ? '#d1fae5' : '#f1f5f9',
                }
              }} 
            />
          </Box>
        );
      }
    },
    { 
      field: 'created_at', 
      headerName: 'Registered On', 
      flex: 1, 
      minWidth: 160,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
          <Box sx={{ bgcolor: '#f1f5f9', p: 0.8, borderRadius: '8px', display: 'flex' }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: '#64748b' }} />
          </Box>
          <Typography variant="body2" color="#475569" fontWeight={600}>
            {params.value ? new Date(params.value).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
          <Tooltip title="Quick Edit Guest (Syncs across all inquiries & leads)">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Container maxWidth={false} sx={{ minHeight: '100vh', py: 2.5, px: { xs: 2, md: 3 }, bgcolor: '#f0f4f8', display: 'flex', flexDirection: 'column', gap: 3 }}>
      
      {/* ── Premium Header ── */}
      <Box sx={{
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #0ea5e9 100%)',
        p: { xs: 3, md: '28px 36px' },
        boxShadow: '0 8px 32px rgba(30,64,175,0.25)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -60, right: -60,
          width: 220, height: 220, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.07)'
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -40, right: 120,
          width: 130, height: 130, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.05)'
        }
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <VerifiedUserIcon fontSize="large" /> Easo Guests
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)', mt: 0.8, fontWeight: 500 }}>
              View and manage all your verified guests who have successfully booked a trip.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={16} sx={{ color: '#1e40af' }} /> : <VerifiedUserIcon />}
              onClick={handleSyncConfirmed}
              disabled={syncing || loading}
              sx={{
                borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 2.5, py: 1,
                bgcolor: '#fff', color: '#1e40af',
                '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-1px)' },
                boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {syncing ? 'Syncing...' : 'Sync Confirmed Leads'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchEasoGuests}
              disabled={loading || syncing}
              sx={{
                borderRadius: '12px', textTransform: 'none', fontWeight: 600, px: 2.5, py: 1,
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                backdropFilter: 'blur(8px)', bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', borderColor: '#fff', transform: 'translateY(-1px)' },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Refresh Data
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ── Data Table ── */}
      <Paper elevation={0} sx={{ 
        flexGrow: 1, 
        minHeight: 500, 
        width: '100%',
        borderRadius: '20px',
        border: '1px solid #e2e8f0',
        bgcolor: '#fff',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        overflow: 'hidden'
      }}>
        <DataGrid
          rows={guests}
          columns={columns}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={40} thickness={4} sx={{ color: '#3b82f6' }} />
              </Box>
            )
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 15 }
            }
          }}
          pageSizeOptions={[15, 25, 50, 100]}
          disableRowSelectionOnClick
          loading={loading}
          rowHeight={70}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '2px solid #e2e8f0',
              color: '#475569',
              fontWeight: 700,
              fontSize: '0.85rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f1f5f9',
              '&:focus': { outline: 'none' },
              '&:focus-within': { outline: 'none' }
            },
            '& .MuiDataGrid-row': {
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: '#f8fafc',
              }
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc'
            }
          }}
        />
      </Paper>

      {/* ── Trips Modal ── */}
      <Dialog 
        open={tripsModalOpen} 
        onClose={() => setTripsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 3, 
          bgcolor: '#f8fafc', 
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="h5" fontWeight="800" color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExploreIcon color="primary" /> Guest Trips
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Trips history for <strong>{selectedGuest?.name}</strong>
            </Typography>
          </Box>
          <IconButton onClick={() => setTripsModalOpen(false)} sx={{ bgcolor: '#e2e8f0', '&:hover': { bgcolor: '#cbd5e1' } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: '#f1f5f9' }}>
          {loadingTrips ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={40} thickness={4} />
            </Box>
          ) : guestTrips.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <FlightTakeoffIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="#64748b">No trips found for this guest</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {guestTrips.map((trip, idx) => (
                <Grid item xs={12} key={trip._id || idx}>
                  <Card 
                    elevation={0} 
                    onClick={() => handleViewSingleTrip(trip._id)}
                    sx={{ 
                      borderRadius: '12px', 
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#3b82f6',
                        boxShadow: '0 4px 12px rgba(59,130,246,0.15)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="primary" fontWeight="700" sx={{ mb: 0.5 }}>
                            Trip #{guestTrips.length - idx}
                          </Typography>
                          <Typography variant="h6" fontWeight="800" color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOnIcon fontSize="small" color="error" />
                            {trip.trip_details?.starting_point} to {trip.trip_details?.ending_point}
                          </Typography>
                          <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight="600">Travel Date</Typography>
                              <Typography variant="body2" fontWeight="600" color="#334155">
                                {trip.trip_details?.travel_date ? new Date(trip.trip_details.travel_date).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight="600">Duration</Typography>
                              <Typography variant="body2" fontWeight="600" color="#334155">
                                {trip.trip_details?.duration} Days
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight="600">Pax</Typography>
                              <Typography variant="body2" fontWeight="600" color="#334155">
                                {(trip.trip_details?.number_of_adults || 0) + (trip.trip_details?.number_of_children || 0)} Guests
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>

                        <Stack alignItems="flex-end" spacing={1.5}>
                          <Chip 
                            label={`₹${trip.trip_details?.price || 0}`} 
                            color="success" 
                            variant="outlined"
                            sx={{ fontWeight: '800', fontSize: '1rem', bgcolor: '#f0fdf4' }}
                          />
                          <Button 
                            endIcon={<ArrowForwardIosIcon sx={{ fontSize: '12px !important' }} />}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            View Details
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Edit Guest Dialog ── */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Quick Edit Guest</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Updating this guest info automatically syncs across all related Inquiries, Verified Leads, and Quotations.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Guest Name"
              size="small"
              fullWidth
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              label="Phone Number"
              size="small"
              fullWidth
              value={editForm.phone}
              onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
            />
            <TextField
              label="Email Address"
              size="small"
              fullWidth
              value={editForm.email}
              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              label="Address"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={editForm.address}
              onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={savingEdit || !editForm.name}
            sx={{ fontWeight: 700 }}
          >
            {savingEdit ? <CircularProgress size={20} color="inherit" /> : 'Save & Sync Everywhere'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar Alert ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EasoGuestsPage;
